import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

export const RUN_SOURCE_LABEL = {
  intercom: "Intercom",
  helpjuice: "Helpjuice (live)",
  helpjuice_export: "Helpjuice export",
};

const ACTION_STYLE = {
  new: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  changed: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  restored: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  skipped_empty: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  failed: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
};
const ACTION_LABEL = { new: "added", changed: "changed", restored: "restored", skipped_empty: "empty", failed: "failed" };

const Count = ({ label, value, tone, testid }) => (
  <span data-testid={testid} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
    {value} {label}
  </span>
);

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const c = run.counts || {};
  const when = run.started_at ? new Date(run.started_at).toLocaleString() : "";
  const items = run.items || [];
  return (
    <Card className="overflow-hidden p-0" data-testid={`kb-import-run-${run.run_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} disabled={!items.length}
        className="flex w-full flex-wrap items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-900/40 disabled:cursor-default"
        data-testid={`kb-import-run-toggle-${run.run_id}`}>
        {items.length ? (open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />)
          : <span className="h-4 w-4 shrink-0" />}
        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {RUN_SOURCE_LABEL[run.source] || run.source}
        </span>
        <span className="text-xs text-slate-600 dark:text-slate-300">{when}</span>
        {run.by && run.by !== "system" && <span className="text-[11px] text-slate-400">by {run.by}</span>}
        {run.running ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-sky-600" data-testid={`kb-import-run-running-${run.run_id}`}>
            <Loader2 className="h-3 w-3 animate-spin" /> running…
          </span>
        ) : run.status === "error" ? (
          <span className="text-[11px] text-rose-600" title={run.error} data-testid={`kb-import-run-error-${run.run_id}`}>failed</span>
        ) : run.status === "interrupted" ? (
          <span className="text-[11px] text-amber-600" title={run.error} data-testid={`kb-import-run-interrupted-${run.run_id}`}>interrupted — run again</span>
        ) : null}
        <span className="ml-auto flex flex-wrap gap-1">
          <Count label="new (review)" value={c.new || 0} tone={ACTION_STYLE.new} testid={`kb-import-run-new-${run.run_id}`} />
          <Count label="changed" value={c.changed || 0} tone={ACTION_STYLE.changed} testid={`kb-import-run-changed-${run.run_id}`} />
          <Count label="unchanged" value={(c.unchanged || 0) + (c.baseline || 0)} tone={ACTION_STYLE.skipped_empty} testid={`kb-import-run-unchanged-${run.run_id}`} />
          {!!c.skipped_empty && <Count label="empty" value={c.skipped_empty} tone={ACTION_STYLE.skipped_empty} />}
          {!!c.failed && <Count label="failed" value={c.failed} tone={ACTION_STYLE.failed} />}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2" data-testid={`kb-import-run-items-${run.run_id}`}>
          {run.items_total > items.length && (
            <p className="mb-1 text-[11px] text-slate-400">Showing the last {items.length} of {run.items_total} items.</p>
          )}
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((it, k) => (
              <li key={`${it.article_id}-${k}`} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${ACTION_STYLE[it.action] || ACTION_STYLE.skipped_empty}`}>
                  {ACTION_LABEL[it.action] || it.action}
                </span>
                {it.slug ? (
                  <Link to={`/kb/a/${it.slug}`} target="_blank" className="min-w-0 flex-1 truncate text-primary hover:underline">
                    {it.name || it.slug}
                  </Link>
                ) : <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{it.name || it.article_id}</span>}
                {(it.old_hash || it.new_hash) && (
                  <span className="font-mono text-[10px] text-slate-400" title="content hash before → after">
                    {it.old_hash ? `${it.old_hash} → ` : ""}{it.new_hash}
                  </span>
                )}
                {it.note && <span className="text-[11px] text-slate-400">{it.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** The import ledger — one row per run with what it added, flagged or skipped. */
export const KbImportRunsPanel = ({ refreshKey = 0 }) => {
  const [runs, setRuns] = useState(null);
  useEffect(() => {
    api.get("/kb/import/runs").then((r) => setRuns(r.data.runs || [])).catch(() => setRuns([]));
  }, [refreshKey]);
  return (
    <section className="mt-8" data-testid="kb-import-runs">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <History className="h-4 w-4 text-slate-400" /> Recent import runs
      </h2>
      <p className="mb-3 text-xs text-slate-500">
        Nothing is added on its own: new source articles and articles whose words changed at the source are both parked above for review, and nothing is ever written over. Each item shows its content hash before → after.
      </p>
      {runs === null ? (
        <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
      ) : runs.length === 0 ? (
        <Card className="p-4 text-xs text-slate-500" data-testid="kb-import-runs-empty">No import has run yet.</Card>
      ) : (
        <div className="space-y-2">{runs.map((r) => <RunRow key={r.run_id} run={r} />)}</div>
      )}
    </section>
  );
};
